import clsx from 'clsx';
import { FC, useState } from 'react';

import { Button, Title, Stack } from '../../..';
import { FileUploader } from '../../../fileUploader';
import style from './genericFileUploadModalContent.module.scss';
import { FileUploadResultProps } from '../fileUploadUtils';

interface Props {
  uploadCb: (payload: any) => any;
  onUploadDone?: any;
  id?: string;
  fileNamesToCompare?: string[];
}

const ERROR_CODES: Record<string, string> = {
  FILE_INFECTED: 'this file is infected with malware or a virus.',
  FILE_ALREADY_EXISTS: 'file with the same name already exists.',
  INVALID_FILE_FORMAT: 'Accepted formats: JPG, JPEG, PNG, HEIC, PDF, DOC, DOCX',
  FILE_IS_TOO_LARGE: 'file must be less than 10MB.'
};

const ErrorMessage = ({ message, boldText }: { message: string; boldText?: string }) => (
  <>
    <Stack className={style.errorText} alignItems="center">
      <span className={clsx('material-icons-outlined')}>error_outline</span>
    </Stack>
    <div className={style.errorText}>
      {boldText && <span className={style.bold}>{boldText}</span>}
      {message}
    </div>
  </>
);

const GenericFileUploadModalContent: FC<Props> = ({
  uploadCb,
  onUploadDone,
  fileNamesToCompare,
  id
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadDone, setIsUploadDone] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [fileToReplace, setFileToReplace] = useState<File>();
  const [uploadedFilename, setUploadedFilename] = useState<string>();
  const [showProgressBarScreen, setShowProgressBarScreen] = useState(false);

  const uploadFile = async (file: File) => {
    const data = new FormData();
    data.append('file', file);

    setIsLoading(true);
    setShowProgressBarScreen(true);
    try {
      const uploadedFileMetadata: FileUploadResultProps = await uploadCb({ id, data });
      setUploadedFilename(file.name);
      onUploadDone(uploadedFileMetadata);
      setUploadError('');
      setIsUploadDone(true);
    } catch (error: any) {
      if (error.response?.data?.message === 'Request Entity Too Large') {
        setUploadError(ERROR_CODES.FILE_IS_TOO_LARGE);
      } else {
        setUploadError(
          ERROR_CODES[error?.response?.data?.errorCode] ||
            error?.response?.data?.detail ||
            error?.message
        );
      }
      setIsUploadDone(false);
      onUploadDone(null);

      return null;
    } finally {
      setIsLoading(false);
      setShowProgressBarScreen(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!file || !id) return;
    setUploadError('');

    const fileNameAlreadyExists = fileNamesToCompare && fileNamesToCompare.includes(file.name);
    if (fileNameAlreadyExists) {
      setFileToReplace(file);
      return;
    }
    uploadFile(file);
  };

  return (
    <>
      <div className={style.uploadContainerTitle}>
        <Title text="Upload file" type="h2" />
      </div>

      <div
        className={clsx(
          style.uploaderContainer,
          showProgressBarScreen && style.loadingLayout,
          isUploadDone && style.isUploadDone
        )}
      >
        {!isUploadDone && !fileToReplace && (
          <FileUploader
            handleFileUploaded={handleFileUpload}
            isLoading={isLoading}
            uploadType="other"
            showProgressBarScreen={showProgressBarScreen}
          />
        )}
        {fileToReplace && (
          <div>
            <ErrorMessage message="A file with this name already exists. Do you want to replace it?" />
            <Stack flexDirection="row" gap={2} mx={3}>
              <Button
                text="Cancel"
                theme="transparent"
                handleOnClick={() => {
                  setFileToReplace(undefined);
                }}
              />
              <Button
                text="Replace"
                theme="primary"
                handleOnClick={() => {
                  setFileToReplace(undefined);
                  uploadFile(fileToReplace);
                }}
              />
            </Stack>
          </div>
        )}
        {uploadError && <ErrorMessage message={uploadError} boldText="File upload failed: " />}
        {isUploadDone && !uploadError && (
          <div className={style.uploadDoneContainer}>
            <div className={style.uploadDoneContainerIcon}>
              <span className={clsx('material-icons-outlined')}>check_circle</span>
            </div>
            Successfully uploaded {uploadedFilename}
          </div>
        )}
      </div>
    </>
  );
};

export default GenericFileUploadModalContent;
